import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { notify } from "@organisms/notify";

import type { INotifyPromiseProps } from "@organisms/notify";

type INotifyPromiseOptions<TData, TError> = Partial<
  Pick<INotifyPromiseProps<TData, TError>, "pending" | "error" | "success">
>;

interface IApiMessage {
  code?: string;
  message?: string;
}

export function getSuccessMessage(response: unknown) {
  if (!response || typeof response !== "object") return;

  let responseData: unknown = response;

  if (!("messages" in response) && "data" in response)
    responseData = response.data;

  if (!responseData || typeof responseData !== "object") return;

  const messages = "messages" in responseData ? responseData.messages : null;

  if (!Array.isArray(messages)) return;

  const successMessage = messages.find(
    (item): item is IApiMessage =>
      !!item && typeof item === "object" && item.code === "success",
  );

  return typeof successMessage?.message === "string"
    ? successMessage.message
    : undefined;
}

export function useNotifyPromise() {
  return useCallback(
    <TResponse,>(promise: Promise<TResponse>) =>
      promise.then((response) => {
        const title = getSuccessMessage(response)?.trim();

        if (title) notify.success({ title });

        return response;
      }),
    [],
  );
}

export function useCustomNotifyPromise<T, TE>(
  globalOptions?: INotifyPromiseOptions<T, TE>,
) {
  const { t } = useTranslation("alert");

  return useCallback(
    <TData extends T = T, TError extends TE = TE>(
      promise: Promise<TData>,
      localOptions?: INotifyPromiseOptions<TData, TError>,
    ): Promise<TData> => {
      const options = localOptions || globalOptions;

      // Custom promise notifications need an explicit success title. Mutations
      // without one use useNotifyPromise, which only displays backend messages.
      if (!options?.success?.title) return promise;

      return notify.promise<TData, TError>({
        promise,
        pending: {
          title: t("notif.isProcessing"),
          ...options?.pending,
        },
        error: {
          title: t("notif.errorOccurred"),
          ...options?.error,
        },
        success: {
          ...options?.success,
        },
      });
    },
    [globalOptions, t],
  );
}
